import { useI18n } from "@core/i18n/i18n";
import { GlobalProgramStatus, GlobalProgramStatusType } from "@domain/program/program-status/program-status";
import styled from "@emotion/styled";
import { ProgramStatusIcon, ProgramStatusRenderProps } from "@ui/components/program/program-status-icon";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { computeDiffInDays, now, nowAsStartOfDay, TWO_MONTH_IN_DAYS, TWO_WEEKS_IN_DAYS } from "@utils/date-utils";
import React, { useMemo } from "react";
import { ProgramStatusLabel } from "@ui/components/program/program-status-label";

interface ProgramStatusProps {
	programStatus: GlobalProgramStatus;
	statusLabelVisible?: boolean;
}

export const ProgramStatusComponent: React.FC<ProgramStatusProps> = ({
	programStatus,
	statusLabelVisible = false,
}) => {
	const { formatDurationLeft, format, formatDateRelative, formatDateMed } = useI18n();
	const renderProps: ProgramStatusRenderProps = useMemo(() => {
		const { type } = programStatus;
		switch (type) {
			case GlobalProgramStatusType.OngoingProgram:
			case GlobalProgramStatusType.EndingSoonProgramWithNoNext: {
				const durationLeft = computeDiffInDays(nowAsStartOfDay(), programStatus.endDate);
				const total = computeDiffInDays(programStatus.startDate, programStatus.endDate);
				let pieChartColor;
				if (durationLeft > TWO_MONTH_IN_DAYS) {
					pieChartColor = colors.primary;
				} else if (durationLeft > TWO_WEEKS_IN_DAYS) {
					pieChartColor = colors.yellow;
				} else {
					pieChartColor = colors.rejection;
				}
				return {
					type,
					pieChartColor,
					pieChartProgressPercentage: durationLeft / total,
					label: formatDurationLeft(durationLeft, TWO_MONTH_IN_DAYS),
				};
			}
			case GlobalProgramStatusType.ScheduledProgram:
				return {
					type,
					label: format("global.programStatus.labels.upcomingProgram", {
						formattedDays: programStatus.startDate.startOf("day").to(now().startOf("day"), true),
					}),
				};
			case GlobalProgramStatusType.EndedLastProgram:
				return {
					type,
					label: format("global.programStatus.labels.endedProgramWithNoUpcoming", {
						endDate: formatDateRelative(programStatus.endDate),
					}),
					labelColor: colors.rejection,
				};
			case GlobalProgramStatusType.EndedLastProgramDetailsMode:
				return {
					type,
					label: format("global.programStatus.labels.endedProgramDetailsMode_v2.4", {
						endDate: formatDateMed(programStatus.endDate),
					}),
				};
			case GlobalProgramStatusType.NoProgram:
				return {
					type,
					label: format("global.programStatus.labels.noProgram.v2_6"),
				};
		}
	}, [format, formatDateMed, formatDurationLeft, formatDateRelative, programStatus]);

	return (
		<StatusContainer>
			{statusLabelVisible && <ProgramStatusLabel type={renderProps.type} />}
			<ProgramStatusIcon props={renderProps} />
			<ProgramLabel
				style={{
					color: renderProps.type === GlobalProgramStatusType.EndedLastProgram ? renderProps.labelColor : undefined,
				}}
			>
				{renderProps.label}
			</ProgramLabel>
		</StatusContainer>
	);
};

const StatusContainer = styled.div`
	${row(8, undefined, "center")}
`;

const ProgramLabel = styled.div``;
